import React, { useState, useEffect } from "react";
import { useParams, withRouter } from "react-router";
import { RiLoaderLine, RiAddLine } from "react-icons/ri";
import moment from "moment";
import {
  getAiAppById,
  updateAiAppById,
  uploadAiAppCoverImage,
  getAllAiAppCategories,
} from "@api/seo/aiApp";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Spin,
  Modal,
} from "antd";
import { SuccessNotification, ErrorNotification } from "../..";
import ReactMarkdown from "react-markdown";

// Initial state for the form
const initialState = {
  title: "",
  coverImage: "",
  logo: "",
  description: "",
  content: "",
  category: "",
  publishedDate: "",
  link: "",
  images: [],
};

const AiAppUpdateForm = ({ history }) => {
  const { id } = useParams();

  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Fetch AI App data on component mount
  useEffect(() => {
    fetchAiApp();
  }, []);

  // Fetch AI App data by ID
  const fetchAiApp = async () => {
    setLoading(true);
    try {
      const { data } = await getAiAppById(id);

      // Fetch categories
      const cats = await getAllAiAppCategories();
      setCategories(cats.data);

      // Set category ID
      data.category = data?.category?._id;

      // Convert date to moment object
      if (data.publishedDate) {
        data.publishedDate = moment(
          data.publishedDate?.split("T")[0],
          "YYYY/MM/DD"
        );
      }

      setValues({
        ...data,
        content: data.content,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("GET_AI_APP_BY_ID_ERROR", error);
    }
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();

    setUpdateLoading(true);

    // Validate the form
    if (
      !values.title ||
      !values.description ||
      !values.category ||
      !values.publishedDate ||
      !values.content ||
      !values.link ||
      !values.logo
    ) {
      setUpdateLoading(false);
      return ErrorNotification("Please fill all the required fields");
    }

    updateAiAppById(id, values)
      .then(() => {
        SuccessNotification(`AI App updated successfully`);
        setUpdateLoading(false);
        history.goBack();
      })
      .catch((error) => {
        setUpdateLoading(false);
        ErrorNotification("Error updating AI App");
        console.log("UPDATE_AI_APP_ERROR", error);
      });
  };

  // Validate image before upload
  const beforeImageUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must be smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle image upload (for both cover image and logo)
  const handleUploadImage = async (options, imageType) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    try {
      setLoading(true);
      const uploadFunction =
        imageType === "logo" ? uploadAiAppCoverImage : uploadAiAppCoverImage;
      const res = await uploadFunction(fmData, config);
      setValues({
        ...values,
        [imageType]: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  // Upload button component
  const uploadButton = (
    <div>
      {loading ? (
        <RiLoaderLine
          className="remix-icon-loading remix-icon-spin remix-icon"
          size={24}
        />
      ) : (
        <RiAddLine size={24} />
      )}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Row>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(80vh - 50px)",
            width: "100%",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Col
          span={24}
          style={{ overflowY: "auto", height: "calc(100vh - 100px)" }}
        >
          <Form labelCol={{ span: 4 }} wrapperCol={{ span: 18 }}>
            {/* AI App Title */}
            <Form.Item label="Title" required>
              <Input
                name="title"
                placeholder="Enter title"
                onChange={handleFormChange}
                value={values?.title}
              />
            </Form.Item>

            {/* AI App Logo */}
            <Form.Item label="Logo" required>
              <Upload
                name="logo"
                listType="picture-card"
                customRequest={(options) => handleUploadImage(options, "logo")}
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeImageUpload}
                multiple={false}
                accept="image/*"
              >
                {values?.logo ? (
                  <img
                    src={values?.logo}
                    alt="logo"
                    style={{ width: "100%" }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* AI App Cover Image */}
            <Form.Item label="Cover Image" required>
              <Upload
                name="coverImage"
                listType="picture-card"
                customRequest={(options) =>
                  handleUploadImage(options, "coverImage")
                }
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeImageUpload}
                multiple={false}
                accept="image/*"
              >
                {values?.coverImage ? (
                  <img
                    src={values?.coverImage}
                    alt="cover"
                    style={{ width: "100%" }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* SEO Meta Description */}
            <Form.Item label="Meta Description" required>
              <Input
                name="description"
                placeholder="Enter meta description"
                onChange={handleFormChange}
                value={values?.description}
              />
            </Form.Item>

            {/* Product Link */}
            <Form.Item label="Product Link" required>
              <Input
                name="link"
                placeholder="Enter product link"
                onChange={handleFormChange}
                value={values?.link}
              />
            </Form.Item>

            {/* Category Selection */}
            <Form.Item label="Category" required>
              <Select
                name="category"
                placeholder="Select a category"
                onChange={(value) => setValues({ ...values, category: value })}
                value={values?.category}
              >
                {categories.map((category) => (
                  <Select.Option key={category._id} value={category._id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Publish Date */}
            <Form.Item label="Published Date" required>
              <DatePicker
                className="hp-mb-16 hp-mr-16"
                onChange={(date, dateString) =>
                  setValues({ ...values, publishedDate: dateString })
                }
                value={values?.publishedDate}
              />
            </Form.Item>

            {/* AI App Content */}
            <Form.Item label="Content" required>
              <Col
                style={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <Button
                    onClick={() => setPreviewVisible(true)}
                    style={{ marginRight: "16px" }}
                  >
                    Preview
                  </Button>
                </div>
                <Input.TextArea
                  name="content"
                  placeholder="Enter content in markdown format"
                  onChange={handleFormChange}
                  value={values?.content}
                  rows={16}
                />
              </Col>
            </Form.Item>

            {/* Submit Button */}
            <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
              <Button
                type="primary"
                onClick={handleFormSubmit}
                loading={updateLoading}
              >
                Update
              </Button>
            </Form.Item>
          </Form>
        </Col>
      )}

      {/* Markdown Preview Modal */}
      <Modal
        title="Preview"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <ReactMarkdown>{values?.content}</ReactMarkdown>
      </Modal>
    </Row>
  );
};

export default withRouter(AiAppUpdateForm);
